import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { 
  Upload, FileSpreadsheet, Users, Package, 
  ArrowLeft, CheckCircle, AlertCircle, BarChart3, Truck
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

export default function UploadPage() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState({ customers: false, jobs: false });
  const [results, setResults] = useState({ customers: null, jobs: null });

  const handleCustomerUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, customers: true }));
    setResults(prev => ({ ...prev, customers: null }));
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post("/customers/upload-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResults(prev => ({ ...prev, customers: { success: true, message: res.data.message } }));
      toast.success(res.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload customers";
      setResults(prev => ({ ...prev, customers: { success: false, message } }));
      toast.error(message);
    } finally {
      setUploading(prev => ({ ...prev, customers: false }));
      e.target.value = "";
    }
  }, []);

  const handleJobsUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(prev => ({ ...prev, jobs: true }));
    setResults(prev => ({ ...prev, jobs: null }));
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post("/jobs/upload-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResults(prev => ({ ...prev, jobs: { success: true, message: res.data.message } }));
      toast.success(res.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to upload jobs";
      setResults(prev => ({ ...prev, jobs: { success: false, message } }));
      toast.error(message);
    } finally {
      setUploading(prev => ({ ...prev, jobs: false }));
      e.target.value = "";
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="upload-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <BarChart3 className="h-5 w-5" />
            Dashboard
          </Link>
          <Link to="/admin/jobs" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Package className="h-5 w-5" />
            Delivery Jobs
          </Link>
          <Link to="/admin/customers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Users className="h-5 w-5" />
            Customers
          </Link>
          <Link to="/admin/drivers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Truck className="h-5 w-5" />
            Drivers
          </Link>
          <Link to="/admin/upload" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Upload className="h-5 w-5" />
            Import Data
          </Link>
        </nav>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="font-semibold">Import Data</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Import Data</h1>
            <p className="text-slate-600">Upload CSV or Excel files to bulk import customers and delivery jobs</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Customer CSV Upload */}
            <Card className="card-nassau">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-[#003366]" />
                </div>
                <CardTitle>Import Customers</CardTitle>
                <CardDescription>
                  Upload a CSV file with customer information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg text-sm">
                  <p className="font-medium text-slate-700 mb-2">Required columns:</p>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <code className="bg-slate-200 px-1 rounded">Name</code></li>
                  </ul>
                  <p className="font-medium text-slate-700 mt-3 mb-2">Optional columns:</p>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <code className="bg-slate-200 px-1 rounded">Address Line 1</code>, <code className="bg-slate-200 px-1 rounded">Address Line 2</code>, <code className="bg-slate-200 px-1 rounded">Address Line 3</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">Town/City</code>, <code className="bg-slate-200 px-1 rounded">County</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">Postal Code</code>, <code className="bg-slate-200 px-1 rounded">Country</code></li>
                  </ul>
                </div>
                
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCustomerUpload}
                  className="hidden"
                  id="customer-csv"
                  disabled={uploading.customers}
                  data-testid="customer-csv-input"
                />
                <label htmlFor="customer-csv" className="block">
                  <Button
                    asChild
                    disabled={uploading.customers}
                    className="w-full bg-[#003366] hover:bg-[#002244]"
                  >
                    <span>
                      {uploading.customers ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Customer CSV
                        </>
                      )}
                    </span>
                  </Button>
                </label>
                
                {results.customers && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${
                    results.customers.success ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                  }`}>
                    {results.customers.success ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                    <span className="text-sm">{results.customers.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Jobs Excel Upload */}
            <Card className="card-nassau">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-amber-600" />
                </div>
                <CardTitle>Import Delivery Jobs</CardTitle>
                <CardDescription>
                  Upload an Excel or CSV file with delivery information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg text-sm">
                  <p className="font-medium text-slate-700 mb-2">Required columns:</p>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <code className="bg-slate-200 px-1 rounded">customer_name</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">delivery_address</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">delivery_date</code> (YYYY-MM-DD)</li>
                  </ul>
                  <p className="font-medium text-slate-700 mt-3 mb-2">Optional columns:</p>
                  <ul className="text-slate-600 space-y-1">
                    <li>• <code className="bg-slate-200 px-1 rounded">time_window</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">items_description</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">special_instructions</code></li>
                    <li>• <code className="bg-slate-200 px-1 rounded">customer_email</code>, <code className="bg-slate-200 px-1 rounded">customer_phone</code></li>
                  </ul>
                </div>
                
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleJobsUpload}
                  className="hidden"
                  id="jobs-excel"
                  disabled={uploading.jobs}
                  data-testid="jobs-excel-input"
                />
                <label htmlFor="jobs-excel" className="block">
                  <Button
                    asChild
                    disabled={uploading.jobs}
                    className="w-full bg-[#003366] hover:bg-[#002244]"
                  >
                    <span>
                      {uploading.jobs ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FileSpreadsheet className="h-4 w-4 mr-2" />
                          Upload Jobs Excel/CSV
                        </>
                      )}
                    </span>
                  </Button>
                </label>
                
                {results.jobs && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${
                    results.jobs.success ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                  }`}>
                    {results.jobs.success ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <AlertCircle className="h-5 w-5" />
                    )}
                    <span className="text-sm">{results.jobs.message}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sample Templates */}
          <Card>
            <CardHeader>
              <CardTitle>Need help with the format?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Download sample templates to see the expected format for your CSV/Excel files.
              </p>
              <div className="flex gap-4">
                <Button variant="outline" onClick={() => {
                  const csv = "Name,Address Line 1,Address Line 2,Address Line 3,Town/City,County,Postal Code,Country\nAcme Corporation,123 Main Street,Unit 4,Industrial Estate,Dublin,Dublin,D01 1234,Ireland\n";
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "customers_template.csv";
                  a.click();
                }}>
                  <Users className="h-4 w-4 mr-2" />
                  Customers Template
                </Button>
                <Button variant="outline" onClick={() => {
                  const csv = "customer_name,delivery_address,delivery_date,time_window,items_description,special_instructions\nAcme Corp,456 Oak Ave,2025-01-15,9AM-12PM,Equipment parts,Ring doorbell twice\n";
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "jobs_template.csv";
                  a.click();
                }}>
                  <Package className="h-4 w-4 mr-2" />
                  Jobs Template
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-slate-400">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-slate-400">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex flex-col items-center gap-1 text-slate-400">
            <Users className="h-5 w-5" />
            <span className="text-xs">Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-slate-400">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
